/** Client for the hladom Worker API (spec v0.2 §5). */
import { API_BASE } from './api-base'
import { sessionId } from './session'

export interface RouteConditions {
  effectivePreference: number
  weatherFactor: number | null
  seasonSet: string | null
  hourBucket: number | null
  mode: 'shade' | 'least-effort'
  /** Why the sun term is inert, when it is. */
  reason: 'night' | 'out-of-season' | null
  shadeDataAvailable: boolean
  /** 'none' = zeros, 'synthetic' = the toy model, 'measured' = the M2 pipeline. */
  shadeSource: 'none' | 'synthetic' | 'measured'
  /** Per-input provenance — the headline label alone would misreport a blend. */
  shadeInputs: {
    canopy: 'none' | 'synthetic' | 'measured'
    buildings: 'none' | 'assumed-uniform' | 'measured'
    terrain: 'none' | 'measured'
  }
  terrainDataAvailable: boolean
}

export interface RouteResponse {
  city?: { id: string; name: string }
  geometry: GeoJSON.LineString
  /** Sun exposure per segment, one shorter than `geometry.coordinates`. */
  segmentSun?: number[]
  distanceM: number
  durationS: number
  ascentM: number | null
  descentM: number | null
  /** null until the M2 shade pipeline lands. */
  shadeShare: number | null
  cost: number
  settled: number
  conditions: RouteConditions
  routeMs: number
}

export class ApiError extends Error {
  /** Machine-readable reason, e.g. 'out_of_area' — see the Worker. */
  code?: string
  constructor(message: string, code?: string) {
    super(message)
    this.code = code
  }
}

export async function fetchRoute(
  from: [number, number],
  to: [number, number],
  shadePreference: number,
  time: string,
  signal?: AbortSignal,
): Promise<RouteResponse> {
  const res = await fetch(`${API_BASE}/api/route`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // The session groups one sitting; without it every "sessions" count on
    // the dashboard was counting nulls.
    body: JSON.stringify({ from, to, shadePreference, time, mode: 'walking', session: sessionId }),
    signal,
  })

  let body: unknown
  try {
    body = await res.json()
  } catch {
    throw new ApiError(`API returned ${res.status}`)
  }

  if (!res.ok) {
    const b = body as { error?: string; code?: string }
    throw new ApiError(b?.error ?? `API returned ${res.status}`, b?.code)
  }

  return body as RouteResponse
}

export function formatDuration(seconds: number): string {
  const mins = Math.round(seconds / 60)
  if (mins < 60) return `${mins} min`
  return `${Math.floor(mins / 60)} h ${mins % 60} min`
}

export function formatDistance(metres: number): string {
  return metres < 1000
    ? `${Math.round(metres)} m`
    : `${(metres / 1000).toFixed(metres < 10_000 ? 2 : 1)} km`
}
